import { useContext, useMemo } from "react";
import { UserContext } from "../contexts";

/**
 * "Master Store" write guard.
 *
 * A master admin on Master Store is deliberately UNSCOPED — they see every
 * store's data at once (see TenantContext::currentStoreId on the backend, where
 * a master with no active store resolves to null and the tenant scope adds no
 * WHERE clause). That is exactly what makes cross-store support possible.
 *
 * Reading everything is fine. WRITING from that state is not: with no active
 * store there is no single store to save into, and the backend's write rule
 * (TenantContext::writeStoreId) falls back to the master's OWN store_id_m — the
 * RC master store. So a listing created from Master Store is filed against the
 * master store rather than any real one: invisible to every tenant, and a
 * nuisance to find and re-file afterwards.
 *
 * Rather than let that happen and clean it up later, the write entry points are
 * disabled with an explanation telling the user to switch store first.
 *
 * Non-masters are never blocked — they always have a concrete store, either an
 * active one or their own store_id_m.
 */
export const PICK_STORE_MESSAGE =
  "Switch to a specific store first — “Master Store” spans every store, so there is no single store to save this into.";

/** Label shown wherever the unscoped view needs naming in the UI. */
export const MASTER_STORE_LABEL = "Master Store";

/**
 * Are we in the unscoped "Master Store" view?
 *
 * Master + no active store == Master Store. A non-master with no activeStoreId
 * still has their own store_id_m, so they are never in this state.
 *
 * The single definition of that predicate: the write guard, the sidebar's
 * Analytics gate and the Master Store banner all read it from here, so they
 * cannot drift apart.
 *
 * @returns {boolean}
 */
export function useOnMasterStore() {
  const { user, activeStoreId } = useContext(UserContext) || {};

  return Boolean(user?.is_master_admin) && !activeStoreId;
}

/**
 * @returns {{blocked: boolean, reason: string|undefined, guardProps: object}}
 *   `guardProps` is spreadable straight onto a shared Button or a SubTopbar
 *   action object: {} when allowed, {disabled, title} when blocked.
 */
export function useStoreWriteGuard() {
  const blocked = useOnMasterStore();

  // Memoised on `blocked` alone so guardProps is referentially stable. Call
  // sites spread it into useMemo'd action arrays; a fresh object every render
  // would silently defeat those memos.
  return useMemo(
    () => ({
      blocked,
      reason: blocked ? PICK_STORE_MESSAGE : undefined,
      guardProps: blocked ? { disabled: true, title: PICK_STORE_MESSAGE } : {},
    }),
    [blocked]
  );
}
